/* The one registry of «how it works · what to do» texts.
 *
 * Every page's blue «?» (SectionGuide), Settings → «Как работает система» and
 * the setup checklist read from here, so a section is explained once and the
 * three places cannot drift apart (spec 20260909_onboarding_v2_ru.md §2.3).
 *
 * The texts themselves are i18n keys built from the block id:
 *   guide.<id>.title, guide.<id>.what,
 *   guide.<id>.how.1..how,  guide.<id>.todo.1..todo,
 *   guide.<id>.faq.1.q / .a .. faq
 * tests/integration/test_i18n.py expands these counts, so a new block or a
 * longer list must be translated in every interface language before it ships.
 */

export type GuideId =
  | "dashboard"
  | "connections"
  | "partner-search"
  | "partners"
  | "categories"
  | "products"
  | "prices"
  | "stock"
  | "excise"
  | "orders"
  | "documents"
  | "public-page"
  | "settings"
  | "general"
  | "billing";

export type Guide = {
  id: GuideId;
  icon: string;
  how: number;
  todo: number;
  faq: number;
  /** Where the Settings manual's «Открыть раздел» link goes. */
  href: (slug: string) => string;
  external?: boolean;
};

export const GUIDES: Guide[] = [
  { id: "dashboard", icon: "dashboard", how: 3, todo: 2, faq: 1, href: (s) => `#/t/${s}/dashboard` },
  { id: "connections", icon: "hub", how: 3, todo: 2, faq: 0, href: (s) => `#/t/${s}/partners` },
  { id: "partner-search", icon: "travel_explore", how: 4, todo: 2, faq: 1, href: (s) => `#/t/${s}/search-requests` },
  { id: "partners", icon: "handshake", how: 6, todo: 3, faq: 2, href: (s) => `#/t/${s}/partners` },
  { id: "categories", icon: "account_tree", how: 3, todo: 2, faq: 0, href: (s) => `#/t/${s}/product-categories` },
  // how.7 = every card field edits in the list; how.8 = the filter row the
  // catalogue screens share (2026-09-13).
  { id: "products", icon: "inventory_2", how: 8, todo: 2, faq: 1, href: (s) => `#/t/${s}/products` },
  // «Доступ позиций» folded into Prices (2026-09-16): how.7 = the access
  // checkbox beside each client price and the star mode, how.8 = grey rows
  // for cards not available to order.
  { id: "prices", icon: "price_change", how: 8, todo: 2, faq: 1, href: (s) => `#/t/${s}/prices` },
  // how.1 and todo.1 name both ways goods reach the shelf — a confirmed
  // order *or* a delivery note typed in under Inbound — since a new company
  // has no orders to confirm yet. how.5 = the two filter rows, how.6 = the
  // archive and credit notes putting goods back (2026-09-13), how.7 = the
  // availability status and who sees it (2026-09-16).
  { id: "stock", icon: "warehouse", how: 7, todo: 2, faq: 0, href: (s) => `#/t/${s}/stock` },
  // «Акцизные товары» (owner, 2026-09-29): what the platform keeps of
  // alcohol, coffee and sugar-sweetened drinks, and the reports it hands the
  // accountant for the tax office.
  { id: "excise", icon: "local_bar", how: 5, todo: 3, faq: 1, href: (s) => `#/t/${s}/accounting?section=excise` },
  { id: "orders", icon: "receipt_long", how: 8, todo: 2, faq: 2, href: (s) => `#/t/${s}/orders` },
  { id: "documents", icon: "description", how: 5, todo: 2, faq: 1, href: (s) => `#/t/${s}/orders` },
  { id: "public-page", icon: "public", how: 4, todo: 3, faq: 0, href: (s) => `#/pub/${s}`, external: true },
  { id: "settings", icon: "settings", how: 5, todo: 5, faq: 0, href: (s) => `#/t/${s}/account/company-profile` },
  // Settings → General: the module questions and the «About the company»
  // block (spec task_20260914_company_modules.md).
  { id: "general", icon: "tune", how: 3, todo: 2, faq: 1, href: (s) => `#/t/${s}/account/general` },
  // «Тариф» (spec 20261001_subscription_constructor_plan_ru.md): the
  // constructor, usage and statements — who pays, how the bill is counted,
  // pausing and cancelling. Self-service only: no promise of personal help.
  { id: "billing", icon: "receipt", how: 5, todo: 3, faq: 2, href: (s) => `#/t/${s}/pricing` },
];

export function guideById(id: GuideId): Guide {
  const g = GUIDES.find((x) => x.id === id);
  if (!g) throw new Error(`Unknown guide: ${id}`);
  return g;
}

export const range = (n: number): number[] => Array.from({ length: n }, (_, i) => i + 1);
